import { useEffect, useRef } from 'react';
import type { AdminMessage } from '../../api/types.js';
import { formatDay } from '../../lib/utils.js';
import { MessageBubble } from './MessageBubble.js';
import type { ReactNode } from 'react';

interface MessageListProps {
  messages: AdminMessage[];
  conversationName: string;
  botName: string;
  renderFeedbackButton: (message: AdminMessage) => ReactNode;
}

export function MessageList({ messages, conversationName, botName, renderFeedbackButton }: MessageListProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const shouldScrollRef = useRef(true);

  useEffect(() => {
    if (shouldScrollRef.current && listRef.current) {
      listRef.current.scrollTo({
        top: listRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages]);

  const handleScroll = () => {
    if (!listRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 80;
    shouldScrollRef.current = nearBottom;
  };

  const rows: ReactNode[] = [];
  let lastDay = '';

  for (const message of messages) {
    const day = formatDay(message.createdAt);
    if (day !== lastDay) {
      lastDay = day;
      rows.push(
        <div key={`sep-${message.id}-${day}`} className="message-day-separator">
          <span>{day}</span>
        </div>
      );
    }
    rows.push(
      <MessageBubble
        key={message.id}
        message={message}
        conversationName={conversationName}
        botName={botName}
        feedbackButton={renderFeedbackButton(message)}
      />
    );
  }

  return (
    <div ref={listRef} className="message-list-scroll" onScroll={handleScroll}>
      <div className="message-list-inner">{rows}</div>
    </div>
  );
}
